var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { extendTheme } from '@springcare/sh-component-library';
import { theme } from '@chakra-ui/theme';
import { selectSizes, selectOutlineFieldStyles } from 'shared/components';
import { theme as dsm } from '@springcare/sh-component-library';
// Styles that do not work in the theme file, should be added to src/globalStyleWrapper
// https://chakra-ui.com/docs/styled-system/theming/theme#spacing
// (handy reference but their example is wrong, there's no 'spacing' property on theme.)
// Design System:
// https://www.figma.com/file/cHfN76tiCKNUcNQpT6PrSJ/Design-System-2.0?node-id=4%3A152
var space = {
    px: '1px',
    1: '0.25rem',
    2: '0.5rem',
    2.5: '0.75rem',
    3: '1rem',
    3.5: '1.25rem',
    4: '1.5rem',
    5: '3rem',
    6: '6rem', // 96px
};
// https://chakra-ui.com/docs/styled-system/theming/customize-theme
export var cgxTheme = extendTheme({
    semanticTokens: dsm.cgx.semanticTokens,
    colors: dsm.cgx.colors,
    sizes: dsm.cgx.sizes,
    borderWidths: dsm.cgx.borderWidths,
    radii: dsm.cgx.radii,
    space: dsm.cgx.space,
    fonts: dsm.cgx.fonts,
    fontSizes: dsm.cgx.fontSizes,
    lineHeights: dsm.cgx.lineHeights,
    fontWeights: dsm.cgx.fontWeights,
    textStyles: dsm.cgx.textStyles,
    components: {
        Avatar: dsm.cgx.components.Avatar,
        Button: dsm.cgx.components.Button,
        Badge: dsm.cgx.components.Badge,
        Tag: dsm.cgx.components.Tag,
        Text: dsm.cgx.components.Text,
        Heading: dsm.cgx.components.Heading,
    },
}, {
    space: space,
    components: {
        Avatar: {
            defaultProps: {
                size: 'md',
            },
        },
        // Great reference about what can be themed on each component:
        // https://github.com/chakra-ui/chakra-ui/blob/main/packages/theme/src/components/
        Heading: {
            sizes: {
                // https://www.figma.com/file/cHfN76tiCKNUcNQpT6PrSJ/Design-System-2.0?node-id=4%3A129
                h1: {
                    fontSize: '48px',
                    fontWeight: '400',
                },
                h2: {
                    fontSize: '27px',
                    fontWeight: '600',
                },
                h3: {
                    fontSize: '21px',
                    fontWeight: '600',
                },
                h4: {
                    fontSize: '18px',
                    fontWeight: '600',
                },
            },
        },
        // https://github.com/chakra-ui/chakra-ui/blob/main/packages/theme/src/components/table.ts
        Table: {
            baseStyle: {
                th: {
                    p: 2,
                    textTransform: 'none',
                },
                td: {
                    p: 2,
                },
            },
            sizes: {
                body2: {
                    th: {
                        fontSize: '16px',
                        fontWeight: '600',
                    },
                },
            },
        },
        // https://github.com/chakra-ui/chakra-ui/blob/main/packages/theme/src/components/form.ts
        Form: {
            baseStyle: {
                requiredIndicator: {
                    color: 'green.600',
                },
                helperText: {
                    color: 'gray.700',
                    fontSize: 16,
                    mb: 2,
                },
            },
        },
        FormLabel: {
            baseStyle: {
                fontSize: '16px',
                fontWeight: '600',
            },
            variants: {
                required: {
                    _after: {
                        content: '"*"',
                        color: 'primary-base',
                    },
                    _invalid: {
                        color: 'negative-base',
                    },
                },
            },
            sizes: {
                h3: {
                    fontSize: '21px',
                    fontWeight: '600',
                },
                h4: {
                    fontSize: '18px',
                    fontWeight: '600',
                },
                body2: {
                    fontSize: '16px',
                    fontWeight: '600',
                },
            },
        },
        Text: {
            baseStyle: {
                fontSize: '16px',
                fontWeight: '400',
            },
            sizes: {
                subtitle: {
                    fontSize: '16px',
                    fontWeight: '600',
                },
                body1: {
                    fontSize: '16px',
                    fontWeight: '400',
                },
                body2: {
                    fontSize: '16px',
                    fontWeight: '600',
                },
                small: {
                    fontSize: '14px',
                    fontWeight: '400',
                },
                Text3: {
                    fontSize: '21px',
                    fontWeight: '600',
                },
            },
        },
        // https://github.com/chakra-ui/chakra-ui/blob/main/packages/theme/src/components/input.ts
        Input: {
            baseStyle: {
                field: {
                    // https://github.com/chakra-ui/chakra-ui/blob/main/packages/theme/src/components/input.ts#L86
                    // 'bg' seems to default to inherit, and we can't set a color after that.
                    // setting it undefined seems to allow us to then set a background color. :eye-roll:
                    bg: undefined,
                    backgroundColor: 'white',
                },
            },
            sizes: {
                md: {
                    field: { height: '56px', borderRadius: '4px' },
                },
            },
            variants: {
                outline: {
                    field: {
                        padding: '16px',
                        borderColor: 'gray.350',
                        _placeholder: {
                            color: 'gray.500',
                        },
                        _hover: {
                            borderColor: 'gray.400',
                            _placeholder: {
                                color: 'gray.700',
                            },
                            _invalid: {
                                borderColor: 'red.500',
                                backgroundColor: 'red.100',
                                color: 'red.500',
                                _placeholder: { color: 'red.500' },
                            },
                        },
                        _focus: {
                            borderColor: 'gray.400',
                            boxShadow: "0 0 0 2px #B4B9BB",
                            _invalid: {
                                borderColor: 'red.500',
                                backgroundColor: 'red.100',
                                color: 'red.500',
                                _placeholder: { color: 'red.500' },
                            },
                        },
                        _invalid: {
                            borderColor: 'red.500',
                            backgroundColor: 'red.100',
                            color: 'red.500',
                            boxShadow: "0 0 0 2px #AE3131",
                            _placeholder: { color: 'red.500' },
                        },
                        _disabled: {
                            borderColor: 'gray.350',
                        },
                    },
                },
            },
        },
        // https://github.com/chakra-ui/chakra-ui/blob/main/packages/components/theme/src/components/number-input.ts
        NumberInput: {
            baseStyle: {
                field: {
                    bg: undefined,
                    backgroundColor: 'white',
                },
            },
            sizes: {
                md: {
                    field: { height: '56px', borderRadius: '4px' },
                },
            },
            variants: {
                outline: {
                    field: {
                        padding: '16px',
                        borderColor: 'gray.350',
                        _placeholder: {
                            color: 'gray.500',
                        },
                        _hover: {
                            borderColor: 'gray.400',
                            _placeholder: {
                                color: 'gray.700',
                            },
                            _invalid: {
                                borderColor: 'red.500',
                                _placeholder: { color: 'red.500' },
                            },
                        },
                        _focus: {
                            borderColor: 'gray.400',
                            boxShadow: "0 0 0 2px #B4B9BB",
                            _invalid: {
                                borderColor: 'red.500',
                                _placeholder: { color: 'red.500' },
                            },
                        },
                        _invalid: {
                            borderColor: 'red.500',
                            boxShadow: "0 0 0 2px #AE3131",
                            _placeholder: { color: 'red.500' },
                        },
                        _disabled: {
                            borderColor: 'gray.350',
                        },
                    },
                },
            },
        },
        Textarea: {
            variants: {
                outline: {
                    lineHeight: '24px',
                    padding: '16px',
                    minHeight: '80px',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    borderColor: 'gray.350',
                    _placeholder: {
                        color: 'gray.500',
                    },
                    _hover: {
                        borderColor: 'gray.400',
                        _placeholder: {
                            color: 'gray.700',
                        },
                    },
                    _focus: {
                        borderColor: 'gray.400',
                        boxShadow: "0 0 0 2px #B4B9BB",
                    },
                    _invalid: {
                        borderColor: 'red.500',
                        boxShadow: "0 0 0 2px #AE3131",
                    },
                    _disabled: {
                        borderColor: 'gray.350',
                    },
                },
            },
        },
        // NOTE: Use FormSelect or DropDown instead of chakra's Select
        // Although Select merges its sizes, defaultProps, variants, and field baseStyle from the Input component,
        // they don't seem to carry over. But the styles do mirror those of Input.
        // See https://github.com/chakra-ui/chakra-ui/discussions/2452#discussioncomment-126158
        // As for DropDown, it merges Input and other theme styles with its own chakraStyles prop.
        // See https://github.com/csandman/chakra-react-select#chakrastyles
        Select: {
            // baseStyle doesn't ever seem to apply styles for Select, so leaving it out
            sizes: selectSizes,
            variants: {
                outline: function (props) { return ({
                    field: selectOutlineFieldStyles,
                    icon: {
                        color: props.isInvalid ? 'red.500' : 'currentColor',
                    },
                }); },
                headerSelect: {
                    field: {
                        paddingStart: 0,
                        paddingInlineStart: 0,
                        paddingLeft: 0,
                        border: 'none',
                        fontSize: '27px',
                        fontWeight: '600',
                    },
                },
                pageSelect: {
                    field: {
                        paddingStart: 0,
                        paddingInlineStart: 0,
                        paddingLeft: 0,
                        paddingBottom: 4,
                        border: 'none',
                        fontSize: '27px',
                        fontWeight: '600',
                        height: 'auto',
                        background: 'transparent',
                        cursor: 'pointer',
                    },
                },
            },
        },
        Radio: {
            sizes: {
                md: {
                    control: { width: '20px', height: '20px' },
                },
            },
            baseStyle: {
                control: {
                    borderColor: 'gray.700',
                    bg: 'none',
                    _checked: {
                        borderColor: 'green.500',
                        bg: 'none',
                        color: 'green.500',
                        _hover: {
                            borderColor: 'green.500',
                            bg: 'none',
                        },
                        _before: {
                            width: '10px',
                            height: '10px',
                        },
                    },
                    _focus: {
                        _focus: {
                            boxShadow: 'none',
                        },
                    },
                },
                container: {
                    borderRadius: '100%',
                },
            },
            variants: {
                // Global style for box-shadow on focus can be found in src/globalStyleWrapper
                radiopill: {
                    label: {
                        color: 'gray.700',
                    },
                    container: {
                        paddingTop: 2,
                        paddingBottom: 2,
                        paddingLeft: 3,
                        paddingRight: 3,
                        borderColor: 'transparent',
                        borderWidth: '2px',
                        borderRadius: '100px',
                        _hover: {
                            borderColor: 'green.500',
                            borderWidth: '2px',
                            borderRadius: '100px',
                        },
                        _checked: {
                            borderColor: 'green.500',
                            borderWidth: '2px',
                            borderRadius: '100px',
                            backgroundColor: 'rgba(6, 132, 100, .1)',
                        },
                        _invalid: {
                            borderColor: 'red.500',
                            borderWidth: '2px',
                            backgroundColor: 'red.100',
                            _hover: {
                                backgroundColor: 'red.100',
                                borderColor: 'red.500',
                            },
                            _checked: {
                                backgroundColor: 'red.100',
                                borderColor: 'red.500',
                                borderWidth: '2px',
                            },
                        },
                    },
                    control: {
                        _checked: {
                            _invalid: {
                                color: 'red.500',
                                backgroundColor: 'transparent',
                            },
                        },
                    },
                },
            },
            defaultProps: {
                variant: 'radiopill',
            },
        },
        Checkbox: {
            sizes: {
                md: {
                    control: { width: '20px', height: '20px' },
                },
            },
            baseStyle: {
                control: {
                    borderColor: 'gray.700',
                    borderRadius: '4px',
                    bg: 'none',
                    _checked: {
                        borderColor: 'green.500',
                        bg: 'none',
                        color: 'green.500',
                        _hover: {
                            borderColor: 'green.500',
                            bg: 'none',
                        },
                    },
                    _focus: {
                        _focus: {
                            boxShadow: 'none',
                        },
                    },
                },
                container: {
                    borderRadius: '4px',
                },
            },
            variants: {
                // Global style for box-shadow on focus can be found in src/globalStyleWrapper
                checkpill: {
                    label: {
                        color: 'gray.700',
                    },
                    container: {
                        paddingTop: 2,
                        paddingBottom: 2,
                        paddingLeft: 3,
                        paddingRight: 3,
                        borderColor: 'transparent',
                        borderWidth: '1px',
                        borderRadius: '4px',
                        _hover: {
                            borderColor: 'green.500',
                            borderWidth: '1px',
                            borderRadius: '4px',
                        },
                        _checked: {
                            borderColor: 'green.500',
                            borderWidth: '1px',
                            borderRadius: '4px',
                            backgroundColor: 'rgba(6, 132, 100, .1)',
                        },
                    },
                },
                noborder: {
                    label: {
                        _checked: {
                            color: 'green.500',
                        },
                    },
                    _checked: {
                        border: 'none',
                    },
                },
            },
            defaultProps: {
                variant: 'checkpill',
            },
        },
        Switch: {
            baseStyle: {
                track: {
                    bg: 'gray.500',
                    _checked: {
                        bg: 'green.500',
                    },
                },
            },
        },
        Button: {
            baseStyle: {
                borderRadius: '4px',
                margin: '2px',
                borderWidth: 0,
                fontFamily: 'HKGroteskRegular, sans-serif',
            },
            variants: {
                primary: {
                    color: 'white',
                    backgroundColor: 'green.500',
                    _disabled: {
                        color: 'gray.400',
                        backgroundColor: 'gray.350',
                        opacity: 1,
                    },
                    _hover: {
                        backgroundColor: 'green.600',
                        _disabled: {
                            color: 'gray.400',
                            backgroundColor: 'gray.350',
                            opacity: 1,
                        },
                    },
                    _active: {
                        backgroundColor: 'green.600',
                    },
                    border: 'none',
                },
                secondary: {
                    border: '2px',
                    color: 'green.600',
                    _hover: {
                        backgroundColor: 'green.100',
                    },
                },
                tertiary: {
                    border: '2px',
                    color: 'brown.500',
                    _hover: {
                        backgroundColor: 'brown.200',
                    },
                },
                links: {
                    border: 'none',
                    textDecorationLine: 'none',
                    color: 'green.500',
                    _hover: {
                        color: 'green.600',
                    },
                },
                blueLinks: {
                    border: 'none',
                    color: 'blue.500',
                    _hover: {
                        color: 'blue.600',
                    },
                },
                redLinks: {
                    border: 'none',
                    color: 'red.500',
                    _hover: {
                        color: 'error.dark',
                    },
                },
            },
            defaultProps: {
                variant: 'primary',
            },
        },
        Divider: {
            baseStyle: {
                backgroundColor: 'gray.400',
                height: '1px',
            },
        },
        /**
         * Modal is a multi-part component. See the link for more info.
         * https://chakra-ui.com/docs/styled-system/theming/component-style#styling-multipart-components
         *
         * Modal styles: https://github.com/chakra-ui/chakra-ui/blob/main/packages/theme/src/components/modal.ts
         */
        Modal: {
            sizes: {
                md: {
                    dialog: {
                        maxW: '458px',
                    },
                },
                xl: {
                    dialog: {
                        maxW: '600px',
                    },
                },
            },
            variants: {
                confirm: {
                    dialog: {
                        p: 5,
                        justifyContent: 'space-between',
                    },
                    header: {
                        display: 'flex',
                        justifyContent: 'space-between',
                        p: 0,
                        flex: 1,
                        fontSize: '1.75rem',
                    },
                    closeButton: {
                        position: 'static',
                        width: 4,
                        marginY: 'auto',
                        color: 'green.500',
                    },
                    body: {
                        p: 0,
                        my: 3,
                        fontWeight: '600',
                    },
                    footer: {
                        columnGap: 3,
                        p: 0,
                        mt: 2,
                        justifyContent: 'space-between',
                        button: {
                            borderRadius: '3px',
                            flex: '1 1 0px',
                        },
                    },
                },
            },
        },
        /**
         * Accordion is a multi-part component. See the link for more info.
         * https://chakra-ui.com/docs/styled-system/theming/component-style#styling-multipart-components
         */
        Accordion: {
            /**
             * From chakra code: https://github.com/chakra-ui/chakra-ui/blob/next/packages/anatomy/src/index.ts
             *
             * **Accordion anatomy**
             * - Root (Accordion): the root container of the accordion
             * - Container (AccordionItem): the accordion item contains the button and panel
             * - Button (AccordionButton): the button is the trigger for the panel
             * - Panel (AccordionPanel): the panel is the content of the accordion item
             * - Icon (AccordionIcon): the expanded/collapsed icon
             */
            baseStyle: {
                root: {
                    backgroundColor: 'brown.200',
                    mb: 4,
                },
                container: {
                    border: 'none',
                    boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.15)',
                    borderRadius: '4px',
                },
                button: {
                    color: 'gray.700',
                    p: 4,
                    _hover: {
                        backgroundColor: 'brown.200',
                    },
                },
                panel: {
                    backgroundColor: 'brown.200',
                    pt: 0,
                    pb: 4,
                    // This allows the accessibility box-shadow to show around the header. 3px is the shadow width.
                    mt: '3px',
                },
                // Since we can't replace AccordionIcon without losing its accessibility benefits,
                // we work around the icon's svg styling as best we can here.
                icon: {
                    width: '2rem',
                    height: '2rem',
                },
            },
        },
        Alert: {
            variants: {
                'error-subtle': {
                    container: {
                        h: 'fit-content',
                        w: 'fit-content',
                        bg: 'none',
                        color: 'red.500',
                        p: 0,
                        mt: 2,
                        mb: 3,
                    },
                    icon: {
                        w: 4,
                        h: 4,
                        mr: 0,
                    },
                },
                'info-subtle': {
                    container: {
                        bg: 'info-subtle',
                        color: 'info-on-subtle',
                        p: 3,
                        borderRadius: 8,
                        gap: 3,
                    },
                    icon: {
                        w: 4,
                        h: 4,
                        mr: 0,
                    },
                },
            },
        },
        Progress: {
            baseStyle: {
                track: {
                    bg: '#EFF1F1',
                },
            },
        },
        Tooltip: {
            baseStyle: {
                padding: '8px',
                borderRadius: 'md',
            },
        },
        Tag: {
            variants: {
                subtle: function (props) {
                    var c = props.colorScheme;
                    return {
                        container: {
                            backgroundColor: "".concat(c, ".subtle"),
                            color: "".concat(c, ".onSubtle"),
                        },
                    };
                },
                base: function (props) {
                    var c = props.colorScheme;
                    return {
                        container: {
                            backgroundColor: "".concat(c, ".base"),
                            color: "".concat(c, ".onBase"),
                        },
                    };
                },
            },
            defaultProps: {
                colorScheme: 'accent',
                size: 'md',
                variant: 'subtle',
            },
        },
    },
    styles: {
        global: {
            body: {
                bg: 'gray.300',
            },
        },
    },
    colors: {
        blue: {
            100: '#E9EEF3',
            500: '#205785',
        },
        blueGreen: {
            500: '#0C514F',
        },
        brown: {
            200: '#FAF9F6',
            300: '#DCD9D6',
            400: '#B8ADA1',
            500: '#6A544D',
            600: '#452A25',
        },
        green: {
            100: '#E6F3EF',
            500: '#068464',
            600: '#1C6B57',
        },
        gray: {
            200: '#ECECEC',
            300: '#EFF1F1',
            350: '#E9E9E9',
            400: '#B4B9BB',
            500: '#6C7071',
            600: '#424A4C',
            700: '#182023',
        },
        olive: {
            200: '#E8EAE4',
            300: '#D6D8CE',
            400: '#9EA28A',
        },
        orange: {
            500: '#B06112',
        },
        purple: {
            500: '#414379',
        },
        red: {
            100: '#F7EAEA',
            500: '#AE3131',
        },
        teal: {
            500: '#1B4652',
            600: '#103742',
        },
        yellow: {
            500: '#F2B145',
        },
        info: {
            base: '#316A9A',
            dark: '#1F4769',
            light: '#E9EEF3',
            text: '#205785',
        },
        error: {
            dark: '#8F2B2B',
            light: '#FDDCDC',
            text: '#6F2626',
        },
        success: {
            100: 'red',
            500: '#64CCB2',
            light: '#DFF8F2',
            text: '#145A49',
        },
        warning: {
            light: '#FFF8DC',
            onLight: '#916010',
        },
        tertiary1: {
            light: '#F1EFEA',
            400: '#958A83',
        },
        tertiary3: {
            base: '#CC612E',
            dark: '#A24D25',
        },
        platform: {
            base: '#737778',
            dark: '#555859',
        },
        accent: {
            subtle: '#EFEDE8',
            onSubtle: '#482D28',
            base: '#482D28',
            onBase: '#FBFAF9',
        },
        negative: {
            subtle: '#FDE8E8',
            onSubtle: '#AE3131',
            base: '#C44848',
            onBase: '#FFFFFF',
        },
        content: {
            secondary: '#556065',
        },
    },
    sizes: __assign(__assign(__assign({}, theme.space), space), { max: 'max-content', min: 'min-content', full: '100%', '3xs': '14rem', '2xs': '16rem', xs: '20rem', sm: '24rem', md: '28rem', lg: '32rem', xl: '36rem', '2xl': '42rem', '3xl': '48rem', '4xl': '56rem', '5xl': '64rem', '6xl': '72rem', '7xl': '80rem', '8xl': '90rem', container: {
            sm: '360px',
            md: '768px',
            lg: '1366px',
            xl: '1600px',
        }, fonts: {
            body: 'HKGroteskRegular, sans-serif',
            heading: 'HKGroteskRegular, sans-serif',
        }, textStyles: {
            noData: {
                color: 'gray.500',
            },
            // https://www.figma.com/file/cHfN76tiCKNUcNQpT6PrSJ/Design-System-2.0?node-id=4%3A129
            button: {
                fontSize: '16px',
                fontWeight: '600',
            },
        } }),
});
